import { Select } from "antd";
import { type FC, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import { useTranslation } from "src/translations";
import { SelectInputMulti } from "../../../Select/SelectInputMulti";
import { useLoadConfigQuery } from "../../services/server/formComponentsApi";
import type { AntCustomFormField, SelectDynamicFormField } from "../../types/types";

interface DynamicSelectInputProps extends AntCustomFormField<string[]> {
    name: string;
    label: string;
    readOnly: boolean;
    fieldConfig: SelectDynamicFormField;
    fetchOptionsOnSelectOnly?: boolean;
}

export const MultiSelectDynamic: FC<DynamicSelectInputProps> = ({
    fieldConfig,
    readOnly,
    fetchOptionsOnSelectOnly = false,
    onChange,
    value,
    ...restProps
}) => {
    const [active, setActive] = useState(false);
    const {
        urlToFetchOptions,
        optionDisplayPropName = fieldConfig.optionValuePropName ? fieldConfig.optionValuePropName : "key",
        optionValuePropName = fieldConfig.optionValuePropName ? fieldConfig.optionValuePropName : "value",
        queryParams,
    } = fieldConfig;

    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const params: Record<string, unknown> = {};
    let urlToFetch = urlToFetchOptions;
    if (queryParams) {
        queryParams.forEach((param) =>
            Object.keys(param).forEach((urlParam) => {
                // Map FE url param key to BE query param name
                // @ts-ignore
                const beParam = param[urlParam];

                if (urlParam === "apac") {
                    params[beParam] = location.pathname.includes("/apac");
                    return;
                }

                if (urlParam === "fieldName") {
                    // For fieldName, pass the current field's name as literal value
                    params[beParam] = restProps.name;
                    return;
                }

                // Default: read from URL search params
                params[beParam] = searchParams.get(urlParam);
            }),
        );
        const entries = Object.entries(params)
            .filter(([, v]) => v !== null && v !== undefined)
            .map(([k, v]) => [k, String(v)] as [string, string]);
        const urlSearchParams = new URLSearchParams(Object.fromEntries(entries)).toString();
        urlToFetch = `${urlToFetchOptions}?${urlSearchParams}`;
    }

    const { data, isLoading } = useLoadConfigQuery(urlToFetch, {
        skip: (fetchOptionsOnSelectOnly && !active) || !urlToFetchOptions,
    });

    const options = data?.items || [];
    const sortedOptions = [...options];
    if (sortedOptions.length > 1) {
        sortedOptions.sort((a, b) => {
            if (t(a[optionDisplayPropName]) === t(b[optionDisplayPropName])) {
                return 0;
            }
            return t(a[optionDisplayPropName]) > t(b[optionDisplayPropName]) ? 1 : -1;
        });
    }

    const onClicked = () => setActive(true);

    const onValueChange = (selectedOptions: string[]) => {
        if (onChange) {
            onChange(selectedOptions);
        }
    };

    return (
        <SelectInputMulti
            isLoading={isLoading}
            onClick={onClicked}
            disabled={readOnly}
            onChange={onValueChange}
            value={value}
            {...restProps}
        >
            {sortedOptions
                .filter((option) => option.value)
                .map((option: Record<string, string>) => {
                    return (
                        <Select.Option
                            key={option[optionValuePropName]}
                            value={option[optionValuePropName]}
                            displayName={t(option[optionDisplayPropName])}
                        >
                            {t(option[optionDisplayPropName])}
                        </Select.Option>
                    );
                })}
        </SelectInputMulti>
    );
};
